import { Navigate, useRoutes, useNavigate } from "react-router-dom";
import BaseLayout from "../layouts/BaseLayout";
import AuthLayout from "../layouts/AuthLayout";
import Profile from "../pages/Applicant/Profile/Profile";
import Discovery from "../pages/Discovery/Discovery";
import SettingTemplate from "../pages/SettingTemplate/SettingTemplate";
import ActivateAccount from "../pages/ActivateAccount/ActivateAccount";
import AllTransactions from "../pages/AllTransactions/AllTransactions";
import Drafts from "../pages/Drafts/Drafts";
import EditIssuerTemplate from "../pages/EditIssuerTemplate/EditIssuerTemplate";
import Categories from "../pages/Categories/Categories";
import AllCertificatesPerCategory from "../pages/AllCertificatesPerCategory/AllCertificatesPerCategory";
import CertificateDetails from "../pages/CertificateDetails/CertificateDetails";
import CertificateIssue from "../pages/CertificateIssue/CertificateIssue";
import ViewAllDrafts from "../components/ViewAllDrafts/ViewAllDrafts";
import BatchTransaction from "../pages/BatchTransaction/BatchTransaction";
import CredentialPreview from "../pages/CredentialPreview/CredentialPreview";
import Dashboard from "../pages/Dashboard/Dashboard";
import OrganizationPage from "../pages/OrganizationPage/OrganizationPage";
import TenantApprovalPage from "../pages/TenantApprovalPage/TenantApprovalPage";
import AuthContext from "../store/auth-context";
import { useContext, useEffect } from "react";
import ForgotPasswordAccount from "../pages/ForgotPasswordAccount/ForgotPasswordAccount";
import SuccessEmailIdentity from "../pages/EmailIdentity/SuccessEmailIdentity";
import FailedEmailIdentity from "../pages/EmailIdentity/FailedEmailIdentity";
import VerificationApprovals from "../pages/VerificationApprovals/VerificationApprovals";
import TransactionDetailsPage from "../pages/TransactionDetailsPage/TransactionDetailsPage";
import ProgrammePage from "../pages/ProgrammePage/ProgrammePage";
import BatchPage from "../pages/BatchPage/BatchPage";
import BatchIssuancePage from "../pages/BatchIssuancePage/BatchIssuancePage";
import LegacyIssuancePage from "../pages/LegacyIssuancePage/LegacyIssuancePage";
import LegacyPendingIssuancePage from "../pages/LegacyPendingIssuancePage/LegacyPendingIssuancePage";
import LegacyCertificateDetail from "../pages/LegacyCertificateDetail/LegacyCertificateDetail";
import PrintPdf from "../pages/PrintPdf/PrintPdf";
import NotifyRecipients from "../pages/NotifyRecipients/NotifyRecipients";
import ApprovalDashboard from "../pages/ApprovalDashboard/ApprovalDashboard";
import Tenant from "../pages/Tenant/Tenant";
import AdminUserOrganisation from "../pages/AdminUserOrganisation/AdminUserOrganisation";
import TenantOrganisationPage from "../pages/TenantOrganisationPage/TenantOrganisationPage";
import PostLoginRedirect from "../components/PostLogin/PostLoginRedirect";
import TenantVerificationDetails from "../pages/TenantVerificationDetails/TenantVerificationDetails";
const rolePermissions = {
  admin: [
    "discover",
    "settings",
    "profile",
    "transaction/all",
    "transaction/:transaction_id",
    "certificate/issue/:credential_template_id/:issuer_credential_template_id",
    "certificate/edit/:credential_template_id/:issuer_credential_template_id/:template_type",
    "drafts",
    "drafts/:issuer_credential_template_id",
    "drafts/timestamp/:maxLimit/:currentStartDay/:currentEndDay/:timestamp",
    "approvals/all",
    "approvals/:transaction_id",
    "workspace",
    "workspace/batches/:programmeId",
    "workspace/batches/credentials",
    "workspace/batches/summary/:credentialTemplateId/:issuer_credential_template_id",
    "workspace/batches/legacy-issuance/:issuer_credential_template_id",
    "workspace/batches/legacy-summary/:legacyTransactionId/:issuer_credential_template_id",
    "credentials/:legacyTransactionId/print",
    "credentials/:credential_template_id/:issuer_credential_template_id/notify",
  ],
  issuer: [
    "discover",
    "profile",
    "transaction/all",
    "transaction/:transaction_id",
    "certificate/issue/:credential_template_id/:issuer_credential_template_id",
    "certificate/edit/:credential_template_id/:issuer_credential_template_id/:template_type",
    "settings",
    "workspace",
    "workspace/batches/:programmeId",
    "workspace/batches/credentials",
    "workspace/batches/summary/:credentialTemplateId/:issuer_credential_template_id",
    "workspace/batches/legacy-issuance/:issuer_credential_template_id",
    "workspace/batches/legacy-summary/:legacyTransactionId/:issuer_credential_template_id",
    "credentials/:legacyTransactionId/print",
    "credentials/:credential_template_id/:issuer_credential_template_id/notify",
  ],
  approver: [
    "discover",
    "discover/approvals",
    "profile",
    "approvals/all",
    "approvals/:transaction_id",
    "settings",
  ],
  programme_approver: [
    "discover",
    "discover/approvals",
    "profile",
    "approvals/all",
    "approvals/:transaction_id",
    "settings",
  ],
  tenant_admin: [
    "profile",
    "verifier-approvals",
    "verifier-approvals/:org_code",
    "tenant/:tenant_code/organisations/users",
    "tenant/:tenant_code/organisations/:organisation_code/organisations",
  ],
  tenant_user: [
    "profile",
    "verifier-approvals",
    "verifier-approvals/:org_code",
    "tenant/:tenant_code/organisations/users",
    "tenant/:tenant_code/organisations/:organisation_code/organisations",
  ],
  super_admin: [
    "profile",
    "tenant",
    "tenant/:tenant_code/organisations/users",
    "tenant/:tenant_code/organisations/:organisation_code/organisations",
  ],
};
const ProtectedRoute = ({ children, allowedRoles }) => {
  const authContext = useContext(AuthContext);
  const { isLoading, isLoggedIn, userType } = authContext; // Add isLoading
  const currentPath = window.location.pathname.split("/issuer/")[1] || "";
  const navigate = useNavigate();

  const userRole =
    userType?.toLowerCase() || localStorage.getItem("userType")?.toLowerCase();

  useEffect(() => {
    if (
      (userRole === "programme_approver" || userRole === "approver") &&
      currentPath === "discover"
    ) {
      navigate("/issuer/discover/approvals", { replace: true });
    }
  }, [userRole, currentPath, navigate]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isLoading && !isLoggedIn) {
    return <Navigate to="/" replace />;
  }

  if (!userRole) {
    return <Navigate to="/" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(userRole)) {
    return <Navigate to="/" replace />;
  }

  if (!allowedRoles) {
    const allowedPaths = rolePermissions[userRole] || [];

    const isPathAllowed = allowedPaths.some((allowedPath) => {
      if (allowedPath === currentPath) return true;
      if (allowedPath.includes(":")) {
        const regex = new RegExp(
          `^${allowedPath.replace(/:[^/]+/g, "[^/]+")}$`
        );
        return regex.test(currentPath);
      }
      return false;
    });

    if (!isPathAllowed) {
      return <Navigate to="/" replace />;
    }
  }

  return children;
};

const Router = () => {
  const authContext = useContext(AuthContext);
  const publicRoutes = [
    {
      path: "",
      element: authContext.isLoggedIn ? <PostLoginRedirect /> : <Discovery />,
    },
    { path: "/discovery", element: <Discovery /> },
    {
      path: "certificate/edit/:credential_template_id",
      element: <CertificateDetails />,
    },
    { path: "categories", element: <Categories /> },
    {
      path: "certificates/:category_name/:category_id",
      element: <AllCertificatesPerCategory />,
    },
    { path: "users/activate/:uuid/:token", element: <ActivateAccount /> },
    {
      path: "users/update-password/:uuid/:token",
      element: authContext.isLoggedIn ? (
        <Dashboard />
      ) : (
        <ForgotPasswordAccount />
      ),
    },
    { path: "/organizations/:organizationCode", element: <OrganizationPage /> },
    {
      path: "/success-email-verify",
      element: <SuccessEmailIdentity />,
    },
    {
      path: "/failure-email-verify",
      element: <FailedEmailIdentity />,
    },
    {
      path: "/credentials/:cert_hash",
      element: <CredentialPreview />,
    },
    {
      path: "/credentials/verify/:cert_hash",
      element: <CredentialPreview />,
    },
  ];
  const auditRoutes = [
    { path: "settings", element: <SettingTemplate /> },
    { path: "profile", element: <Profile /> },
    { path: "tenant", element: <Tenant /> },
    {
      path: "tenant/:tenant_code/organisations/users",
      element: <AdminUserOrganisation />,
    },
    {
      path: "tenant/:tenant_code/organisations/:organisation_code/organisations",
      element: <TenantOrganisationPage />,
    },
    { path: "transaction/all", element: <AllTransactions /> },
    {
      path: "transaction/:transaction_id",
      element: <BatchTransaction />,
    },
    {
      path: "certificate/issue/:credential_template_id/:issuer_credential_template_id",
      element: <CertificateIssue />,
    },
    {
      path: "certificate/edit/:credential_template_id/:issuer_credential_template_id/:template_type",
      element: <EditIssuerTemplate />,
    },
    { path: "drafts", element: <Drafts /> },
    {
      path: "drafts/:issuer_credential_template_id/",
      element: <EditIssuerTemplate />,
    },
    {
      path: "drafts/timestamp/:maxLimit/:currentStartDay/:currentEndDay/:timestamp",
      element: <ViewAllDrafts />,
    },
    { path: "discover", element: <Dashboard /> },
    { element: <Navigate to="/issuer/discover" />, index: true },
    {
      path: "approvals/all",
      element: <VerificationApprovals />,
    },
    {
      path: "verifier-approvals",
      element: <TenantApprovalPage />,
    },
    {
      path: "verifier-approvals/:org_code",
      element: <TenantVerificationDetails />,
    },
    {
      path: "approvals/:transaction_id",
      element: <TransactionDetailsPage />,
    },
    { path: "discover", element: <Dashboard /> },
    { path: "discover/approvals", element: <ApprovalDashboard /> },
    {
      path: "workspace",
      element: <ProgrammePage />,
    },
    {
      path: "workspace/batches/:programmeId",
      element: <BatchPage />,
    },
    {
      path: "workspace/batches/credentials",
      element: <BatchIssuancePage />,
    },
    {
      path: "workspace/batches/summary/:credentialTemplateId/:issuer_credential_template_id",
      element: <LegacyPendingIssuancePage />,
    },
    {
      path: "workspace/batches/legacy-issuance/:issuer_credential_template_id",
      element: <LegacyCertificateDetail />,
    },
    {
      path: "workspace/batches/legacy-summary/:legacyTransactionId/:issuer_credential_template_id",
      element: <LegacyIssuancePage />,
    },
    {
      path: "credentials/:legacyTransactionId/print",
      element: <PrintPdf />,
    },
    {
      path: "credentials/:credential_template_id/:issuer_credential_template_id/notify",
      element: <NotifyRecipients />,
    },
  ];

  const routes = useRoutes([
    {
      path: "/issuer",
      element: <AuthLayout />,
      children: auditRoutes.map((route) => ({
        ...route,
        element: <ProtectedRoute>{route.element}</ProtectedRoute>,
      })),
    },
    {
      path: "/",
      element: <BaseLayout />,
      children: publicRoutes,
    },
    {
      path: "/credentials/verify/:cert_hash",
      element: (
        <ProtectedRoute allowedRoles={["admin", "issuer"]}>
          <CredentialPreview />
        </ProtectedRoute>
      ),
    },

    {
      path: "*",
      element: <Navigate to="/" />, // Redirect to the root path
    },
  ]);

  return routes;
};

export default Router;
